import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, Sparkles } from "lucide-react";
import CoinIcon from "@/components/landing/CoinIcon";
import { motion, useReducedMotion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Waitlist() {
    const reduce = useReducedMotion();
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [platform, setPlatform] = useState("any");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const perks = t("waitlist.perks", { returnObjects: true });
    const safePerks = Array.isArray(perks) ? perks : [];

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const trimmed = email.trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error(t("waitlist.toasts.invalidEmail"));
            return;
        }
        try {
            setSubmitting(true);
            const res = await axios.post(`${API}/waitlist`, { email: trimmed, platform, source: "landing" });
            if (res.data?.already_subscribed) {
                toast.success(t("waitlist.toasts.alreadySubscribed"));
            } else {
                toast.success(t("waitlist.toasts.success"));
            }
            setSubmitted(true);
            setEmail("");
        } catch (err) {
            toast.error(t("waitlist.toasts.error"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="waitlist" className="py-16 sm:py-20 lg:py-24 bg-[#0B4A34] grain-overlay relative" data-testid="waitlist-section">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
                <motion.div initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }} className="rounded-3xl bg-[#FBF7EE] border border-[#E9D9C1] shadow-[0_30px_80px_rgba(6,42,30,0.35)] overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                        <div className="lg:col-span-6 p-6 sm:p-10 lg:p-12">
                            <span className="inline-flex items-center gap-2 rounded-full bg-[#F4EBDD] border border-[#E9D9C1] px-3 py-1 text-xs font-semibold text-[#0B4A34] uppercase tracking-wider"><Sparkles className="h-3.5 w-3.5 text-[#C8A24A]" />{t("waitlist.badge")}</span>
                            <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#062A1E] leading-[1]">
                                {t("waitlist.titleLine1")}<br />
                                <span className="text-[#137A55]">{t("waitlist.titleLine2")}</span>
                            </h2>
                            <p className="mt-4 text-base text-[#1B2621]/80">{t("waitlist.subtitle")}</p>
                            <ul className="mt-5 space-y-2 text-sm text-[#1B2621]/85">
                                {safePerks.map((p, i) => (
                                    <li key={i} className="flex items-center gap-2" data-testid={`waitlist-perk-${i}`}>
                                        {i === 0 ? (
                                            <CoinIcon className="h-5 w-5 flex-shrink-0" />
                                        ) : (
                                            <span className="h-1.5 w-1.5 rounded-full bg-[#C8A24A] ml-1.5" />
                                        )}
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-6 p-6 sm:p-10 lg:p-12 bg-[#F4EBDD]/60 border-t lg:border-t-0 lg:border-l border-[#E9D9C1]">
                            <form onSubmit={onSubmit} className="space-y-4" data-testid="waitlist-form">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email" className="text-sm font-semibold text-[#062A1E]">{t("waitlist.form.emailLabel")}</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B6B63]" />
                                        <Input id="email" type="email" placeholder={t("waitlist.form.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-9 rounded-xl border-[#E9D9C1] bg-[#FBF7EE] text-[#062A1E] focus-visible:ring-2 focus-visible:ring-[#C8A24A]" required data-testid="waitlist-email-input" />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-sm font-semibold text-[#062A1E]">{t("waitlist.form.platformLabel")}</Label>
                                    <div className="grid grid-cols-3 gap-2" role="radiogroup">
                                        {[
                                            { key: "ios", label: t("waitlist.form.platformIos") },
                                            { key: "android", label: t("waitlist.form.platformAndroid") },
                                            { key: "any", label: t("waitlist.form.platformEither") },
                                        ].map((opt) => (
                                            <button type="button" key={opt.key} role="radio" aria-checked={platform === opt.key} onClick={() => setPlatform(opt.key)} className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${platform === opt.key ? "bg-[#0B4A34] text-[#FBF7EE] border-[#0B4A34]" : "bg-[#FBF7EE] text-[#062A1E] border-[#E9D9C1] hover:bg-[#F4EBDD]"}`} data-testid={`waitlist-platform-${opt.key}`}>{opt.label}</button>
                                        ))}
                                    </div>
                                </div>
                                <Button type="submit" disabled={submitting} className="w-full h-12 rounded-xl bg-[#C8A24A] hover:bg-[#E0C06A] text-[#0B0F0D] font-bold text-base shadow-[0_10px_30px_rgba(200,162,74,0.35)]" data-testid="waitlist-submit-button">
                                    {submitting ? t("waitlist.form.submitting") : submitted ? t("waitlist.form.submitted") : t("waitlist.form.submitDefault")}
                                </Button>
                                <p className="flex items-center gap-1.5 text-xs text-[#5B6B63]"><Lock className="h-3.5 w-3.5" />{t("waitlist.form.privacy")}</p>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
